import { useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, MapPin, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Location } from "@/types/location";
import { createLocation } from "@/services/firebase";
import { useToast } from "@/hooks/use-toast";

interface LocationSelectorProps {
  locations: Location[];
  locationsLoading: boolean;
  selectedLocation: string;
  onSelectLocation: (locationName: string) => void;
  canAddLocation?: boolean;
  placeholder?: string;
  initialOrphanLocation?: string; // Orphan location to always show (persists even after clearing)
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  locations,
  locationsLoading,
  selectedLocation,
  onSelectLocation,
  canAddLocation = true,
  placeholder = "Select a location",
  initialOrphanLocation,
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  const commandRef = useRef<HTMLDivElement | null>(null);

  // Sort locations alphabetically
  const activeLocations = useMemo(() =>
    [...locations].sort((a, b) => a.name.localeCompare(b.name)),
    [locations]
  );

  // Compute orphan locations to show (initial orphan + any currently selected orphan)
  const orphanLocations = useMemo(() => {
    const orphans: string[] = [];
    if (initialOrphanLocation && !activeLocations.some(l => l.name === initialOrphanLocation)) {
      orphans.push(initialOrphanLocation);
    }
    if (selectedLocation &&
        !activeLocations.some(l => l.name === selectedLocation) &&
        !orphans.includes(selectedLocation)) {
      orphans.push(selectedLocation);
    }
    return orphans;
  }, [activeLocations, initialOrphanLocation, selectedLocation]);

  const getExactMatchName = (name: string) => {
    const normalized = name.trim().toLowerCase();
    if (!normalized) return null;
    const activeMatch = activeLocations.find(
      loc => loc.name.toLowerCase() === normalized
    )?.name;
    if (activeMatch) return activeMatch;
    const orphanMatch = orphanLocations.find(
      locName => locName.toLowerCase() === normalized
    );
    return orphanMatch ?? null;
  };

  const handleCreateLocation = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || isCreating) return;

    const existingName = getExactMatchName(trimmed);
    if (existingName) {
      onSelectLocation(existingName);
      setSearchValue("");
      setOpen(false);
      return;
    }

    if (!canAddLocation) {
      toast({
        title: "Permission required",
        description: "Only editors and admins can add new locations.",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const created = await createLocation({ name: trimmed, description: "" });
      if (created) {
        toast({
          title: "Location added",
          description: `${trimmed} has been added successfully`,
        });
        onSelectLocation(trimmed);
        setSearchValue("");
        setOpen(false);
      } else {
        toast({
          title: "Error",
          description: "Failed to add location",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding location:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add location",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (locationsLoading) {
    return (
      <Button
        variant="outline"
        className="w-full justify-between"
        disabled
      >
        <span className="text-muted-foreground">Loading locations...</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setSearchValue("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedLocation ? (
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-agri-green" />
              <span>{selectedLocation}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command ref={commandRef}>
          <CommandInput
            placeholder={canAddLocation ? "Search or add locations..." : "Search locations..."}
            value={searchValue}
            onValueChange={setSearchValue}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              const trimmed = searchValue.trim();
              if (!trimmed) return;
              const selectedItem = commandRef.current?.querySelector(
                "[cmdk-item][data-selected='true'], [cmdk-item][aria-selected='true']"
              ) as HTMLElement | null;
              if (selectedItem && selectedItem.dataset.addItem !== "true") {
                return;
              }
              const exactMatch = getExactMatchName(trimmed);
              if (exactMatch) {
                event.preventDefault();
                onSelectLocation(exactMatch);
                setSearchValue("");
                setOpen(false);
                return;
              }
              if (!canAddLocation) return;
              event.preventDefault();
              void handleCreateLocation(trimmed);
            }}
          />
          <CommandList>
            <CommandEmpty>No locations found.</CommandEmpty>
            <CommandGroup>
              {orphanLocations.map((locationName) => (
                <CommandItem
                  key={`orphan-${locationName}`}
                  value={locationName}
                  onSelect={() => {
                    onSelectLocation(selectedLocation === locationName ? "" : locationName);
                    setSearchValue("");
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedLocation === locationName ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <MapPin className="mr-2 h-4 w-4 text-agri-green" />
                  {locationName}
                </CommandItem>
              ))}
              {activeLocations.map((location) => (
                <CommandItem
                  key={location.id}
                  value={location.name}
                  onSelect={() => {
                    onSelectLocation(selectedLocation === location.name ? "" : location.name);
                    setSearchValue("");
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedLocation === location.name ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <MapPin className="mr-2 h-4 w-4 text-agri-green" />
                  {location.name}
                </CommandItem>
              ))}
            </CommandGroup>
            {canAddLocation && searchValue.trim() && !getExactMatchName(searchValue) && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value={`add-${searchValue}`}
                    data-add-item="true"
                    onSelect={() => {
                      void handleCreateLocation(searchValue);
                    }}
                    className="text-agri-green"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add "{searchValue.trim()}"
                  </CommandItem>
                </CommandGroup>
              </>
            )}
            {!canAddLocation && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem disabled className="text-muted-foreground">
                    Only editors and admins can add new locations.
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default LocationSelector;
